/**
 * Arithmetic evaluator for `formula` columns.
 *
 * A formula references other columns by label in braces — `{Budget} * 0.2` —
 * and supports `+ - * / ( )` with unary minus. It is a hand-written parser
 * rather than `eval`/`new Function` because the expression is authored by one
 * user and then evaluated in every other user's browser: anything that reaches
 * a JS engine turns a text column into script injection.
 *
 * Anything the parser cannot resolve — a typo, an unknown column, a division by
 * zero — returns `null`, which the cell renders as empty. A formula is derived
 * data, so a broken one should look like no answer, not throw mid-render.
 */
import type { CustomColumnType } from './useTrackersBoard';

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'ref'; name: string }
  | { kind: 'op'; op: string };

const OPERATORS = new Set(['+', '-', '*', '/', '(', ')']);

function tokenize(expression: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;

  while (i < expression.length) {
    const char = expression[i];

    if (/\s/.test(char)) {
      i += 1;
      continue;
    }

    if (OPERATORS.has(char)) {
      tokens.push({ kind: 'op', op: char });
      i += 1;
      continue;
    }

    if (char === '{') {
      const end = expression.indexOf('}', i);
      if (end === -1) return null;
      tokens.push({ kind: 'ref', name: expression.slice(i + 1, end).trim() });
      i = end + 1;
      continue;
    }

    const number = /^\d+(\.\d+)?/.exec(expression.slice(i));
    if (!number) return null;
    tokens.push({ kind: 'number', value: Number(number[0]) });
    i += number[0].length;
  }

  return tokens;
}

/**
 * Recursive descent over `expression := term (('+' | '-') term)*`, where a term
 * is `factor (('*' | '/') factor)*` and a factor is a number, a reference, a
 * parenthesised expression, or a negated factor.
 */
function parse(tokens: Token[], variables: Map<string, number>): number | null {
  let position = 0;

  const peek = (): Token | undefined => tokens[position];

  const eatOp = (...ops: string[]): string | null => {
    const token = peek();
    if (token?.kind === 'op' && ops.includes(token.op)) {
      position += 1;
      return token.op;
    }
    return null;
  };

  const parseFactor = (): number | null => {
    if (eatOp('-')) {
      const operand = parseFactor();
      return operand === null ? null : -operand;
    }
    if (eatOp('+')) return parseFactor();

    if (eatOp('(')) {
      const inner = parseExpression();
      if (inner === null || !eatOp(')')) return null;
      return inner;
    }

    const token = peek();
    if (token?.kind === 'number') {
      position += 1;
      return token.value;
    }
    if (token?.kind === 'ref') {
      position += 1;
      return variables.get(token.name.toLowerCase()) ?? null;
    }
    return null;
  };

  const parseTerm = (): number | null => {
    let left = parseFactor();
    if (left === null) return null;
    for (let op = eatOp('*', '/'); op !== null; op = eatOp('*', '/')) {
      const right = parseFactor();
      if (right === null) return null;
      if (op === '/' && right === 0) return null;
      left = op === '*' ? left * right : left / right;
    }
    return left;
  };

  function parseExpression(): number | null {
    let left = parseTerm();
    if (left === null) return null;
    for (let op = eatOp('+', '-'); op !== null; op = eatOp('+', '-')) {
      const right = parseTerm();
      if (right === null) return null;
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  const result = parseExpression();
  return position === tokens.length ? result : null;
}

/**
 * Evaluates `expression` against `variables`, keyed by lowercased column label.
 * Returns `null` when the formula cannot be resolved to a finite number.
 */
export function evaluateFormula(
  expression: string | undefined,
  variables: Map<string, number>,
): number | null {
  if (!expression?.trim()) return null;
  const tokens = tokenize(expression);
  if (!tokens || tokens.length === 0) return null;
  const result = parse(tokens, variables);
  return result !== null && Number.isFinite(result) ? result : null;
}

/**
 * Column types a formula can read a number out of. Others hold no numeric
 * value, so referencing one leaves the whole expression unresolvable — which
 * includes `formula` itself: it stores nothing per row.
 */
export const FORMULA_REFERENCEABLE_TYPES: ReadonlySet<CustomColumnType> = new Set<CustomColumnType>(
  ['number', 'progress', 'checkbox'],
);

/**
 * Rewrites `{Old}` references across a board's formulas when a column is
 * renamed. A reference resolves by label, so without this every formula naming
 * the column goes blank the moment someone edits its header — and the cell has
 * no way to say that a rename is why.
 */
export function renameFormulaReferences<T extends { label: string; formulaExpression?: string }>(
  columns: T[],
  from: string,
  to: string,
): T[] {
  if (from.trim() === '' || from === to) return columns;
  const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // eslint-disable-next-line i18next/no-literal-string -- a regex source, not copy
  const reference = new RegExp(`\\{\\s*${escaped}\\s*\\}`, 'gi');

  return columns.map((column) => {
    if (column.formulaExpression === undefined) return column;
    const rewritten = column.formulaExpression.replace(reference, `{${to}}`);
    return rewritten === column.formulaExpression
      ? column
      : { ...column, formulaExpression: rewritten };
  });
}

/**
 * Coerces a cell value to the number a formula can operate on. A box that was
 * never ticked stores nothing at all, so `checkbox` reads absent as 0 —
 * otherwise `{Done} + 1` would resolve on the rows someone had ticked and
 * unticked, and go blank on the ones nobody had touched.
 */
export function toFormulaNumber(value: unknown, type?: CustomColumnType): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (type === 'checkbox' && value == null) return 0;
  return undefined;
}
